/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import { useAuth } from "../context/AuthContext";

/**
 * LiveAudienceTracker — silent, fire-and-forget heartbeat + engagement ping.
 *
 * Privacy contract:
 *   - Never sends IP, keystrokes, form values, exact UA, or any PII beyond
 *     an anonymous session id (rotated monthly) and an optional subscriber
 *     tracking id (only set after the user opts in via newsletter signup).
 *   - Skips entirely for signed-in admins (no heartbeat at all). When a
 *     user is signed in we pass the Bearer token so the backend can
 *     double-check admin status and reject server-side too.
 *   - Skips for /admin/* routes and hidden documents.
 *   - Scroll depth and read time are coarse engagement signals only — no
 *     element-level interaction tracking beyond explicit node/source clicks.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_KEY = "archive_live_sid";
const SESSION_CREATED_KEY = "archive_live_sid_created";
const SUBSCRIBER_KEY = "archive_subscriber_token";
const LAST_SEEN_KEY = "archive_live_last_seen";
const ADMIN_IGNORE_KEY = "archive_admin_ignore_self"; // "on" | "off" — default ON
const HEARTBEAT_INTERVAL_MS = 10_000;
const SESSION_ROTATE_MS = 30 * 24 * 60 * 60 * 1000; // monthly rotation
const RETURN_VISIT_GAP_MS = 30 * 60 * 1000;          // ≥30min gap = return visit

const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, val) => {
  try { localStorage.setItem(key, val); } catch { /* noop */ }
};
const safeRemove = (key) => {
  try { localStorage.removeItem(key); } catch { /* noop */ }
};

const newSessionId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

const getSessionId = () => {
  const existing = safeGet(SESSION_KEY);
  const createdRaw = safeGet(SESSION_CREATED_KEY);
  const created = createdRaw ? Number(createdRaw) : 0;
  const expired = !created || (Date.now() - created) > SESSION_ROTATE_MS;
  if (existing && !expired) return existing;
  // Rotate
  const id = newSessionId();
  safeSet(SESSION_KEY, id);
  safeSet(SESSION_CREATED_KEY, String(Date.now()));
  return id;
};

const dossierSlugFromPath = (pathname) => {
  if (!pathname) return null;
  let m = pathname.match(/^\/topic\/([^/?#]+)/);
  if (m) return m[1];
  m = pathname.match(/^\/epstein-files\/entity\/([^/?#]+)/);
  if (m) return `epstein:${m[1]}`;
  return null;
};

const fireAndForget = (url, payload, authToken) => {
  try {
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
    axios.post(url, payload, { timeout: 5000, headers }).catch(() => { /* silent */ });
  } catch {
    /* silent */
  }
};

// Use sendBeacon when the page is hiding so the last read_time/scroll_depth
// events still make it out. Fallback to fire-and-forget otherwise.
const sendOnUnload = (url, payload) => {
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch { /* fall through */ }
  fireAndForget(url, payload, null);
};

export const LiveAudienceTracker = () => {
  const location = useLocation();
  const { token: authToken, user, loading } = useAuth();
  const heartbeatRef = useRef(null);
  const lastEventPathRef = useRef(null);
  const routeStartRef = useRef(0);
  const maxScrollRef = useRef(0);
  const visibleStartRef = useRef(0);
  const accumulatedDwellRef = useRef(0);

  // 1) Adopt subscriber_token from magic-link `?al=...` and scrub the URL.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const al = sp.get("al");
    if (al && al.length >= 8 && al.length <= 80) {
      safeSet(SUBSCRIBER_KEY, al);
      try {
        axios
          .get(`${API}/audience/identify`, { params: { token: al }, timeout: 5000 })
          .catch(() => { /* silent */ });
      } catch { /* silent */ }
      sp.delete("al");
      const cleanQs = sp.toString();
      const newUrl = window.location.pathname + (cleanQs ? `?${cleanQs}` : "") + window.location.hash;
      try { window.history.replaceState({}, "", newUrl); } catch { /* noop */ }
    }
  }, []);

  const isAdminRoute = (location.pathname || "").startsWith("/admin");
  // "Ignore my activity" — default ON for admins. Admin can flip via the
  // Live Audience panel; we read it lazily so toggling takes effect on
  // next route or interaction without a refresh.
  const isAdmin = !!(user && user.is_admin);
  const adminIgnoreOn = (safeGet(ADMIN_IGNORE_KEY) || "on") !== "off";
  const ignoreSelf = isAdmin && adminIgnoreOn;
  // Wait until auth has resolved before tracking, so we don't briefly count
  // a signed-in admin during cold-load.
  const shouldTrack = !loading && !isAdminRoute && !ignoreSelf;

  const buildPayload = () => ({
    session_id: getSessionId(),
    route: location.pathname || "/",
    dossier_slug: dossierSlugFromPath(location.pathname),
    subscriber_token: safeGet(SUBSCRIBER_KEY) || null,
  });

  // 2) Heartbeat loop — every 10s while the tab is visible.
  useEffect(() => {
    if (!shouldTrack) return;
    if (typeof document !== "undefined" && document.visibilityState === "prerender") return;

    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      fireAndForget(`${API}/audience/heartbeat`, buildPayload(), authToken);
      safeSet(LAST_SEEN_KEY, String(Date.now()));
    };

    tick();
    heartbeatRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    };
  }, [shouldTrack, authToken, location.pathname]);

  // 3) Return-visit detection — fires once per page load if the user has
  //    been gone for ≥30 min.
  useEffect(() => {
    if (!shouldTrack) return;
    const lastSeen = Number(safeGet(LAST_SEEN_KEY) || 0);
    if (lastSeen && (Date.now() - lastSeen) >= RETURN_VISIT_GAP_MS) {
      const payload = buildPayload();
      fireAndForget(`${API}/audience/event`, { ...payload, kind: "return_visit" }, authToken);
    }
    // mark current time so the next reload computes correctly
    safeSet(LAST_SEEN_KEY, String(Date.now()));
  }, [shouldTrack]);

  // 4) Emit one event per distinct route (page_view OR dossier_open).
  //    Also: flush previous route's read_time + scroll_depth before
  //    resetting trackers.
  useEffect(() => {
    if (!shouldTrack) return;
    if (lastEventPathRef.current === location.pathname) return;

    // Flush prior route's engagement metrics, if any.
    const prevPath = lastEventPathRef.current;
    if (prevPath) {
      const dwellNow = (typeof document !== "undefined" && !document.hidden && visibleStartRef.current)
        ? (Date.now() - visibleStartRef.current) / 1000
        : 0;
      const totalDwell = Math.max(0, Math.round(accumulatedDwellRef.current + dwellNow));
      if (totalDwell >= 2) {
        fireAndForget(
          `${API}/audience/event`,
          {
            session_id: getSessionId(),
            route: prevPath,
            dossier_slug: dossierSlugFromPath(prevPath),
            subscriber_token: safeGet(SUBSCRIBER_KEY) || null,
            kind: "read_time",
            value: totalDwell,
          },
          authToken,
        );
      }
      if (maxScrollRef.current > 5) {
        fireAndForget(
          `${API}/audience/event`,
          {
            session_id: getSessionId(),
            route: prevPath,
            dossier_slug: dossierSlugFromPath(prevPath),
            subscriber_token: safeGet(SUBSCRIBER_KEY) || null,
            kind: "scroll_depth",
            value: Math.round(maxScrollRef.current),
          },
          authToken,
        );
      }
    }

    // Reset for the new route.
    lastEventPathRef.current = location.pathname;
    routeStartRef.current = Date.now();
    maxScrollRef.current = 0;
    accumulatedDwellRef.current = 0;
    visibleStartRef.current = (typeof document !== "undefined" && !document.hidden) ? Date.now() : 0;

    const payload = buildPayload();
    const kind = payload.dossier_slug ? "dossier_open" : "page_view";
    fireAndForget(`${API}/audience/event`, { ...payload, kind }, authToken);
  }, [shouldTrack, authToken, location.pathname]);

  // 5) Scroll-depth tracking — throttled, only stores max % reached.
  useEffect(() => {
    if (!shouldTrack || typeof window === "undefined") return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        try {
          const doc = document.documentElement;
          const scrollable = (doc.scrollHeight - window.innerHeight);
          if (scrollable <= 0) return;
          const pct = (window.scrollY / scrollable) * 100;
          if (pct > maxScrollRef.current) maxScrollRef.current = Math.min(100, pct);
        } catch { /* noop */ }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [shouldTrack, location.pathname]);

  // 6) Visibility & unload — accumulate visible-time + flush on leave.
  useEffect(() => {
    if (!shouldTrack || typeof document === "undefined") return;

    const onVisibility = () => {
      if (document.hidden) {
        if (visibleStartRef.current) {
          accumulatedDwellRef.current += (Date.now() - visibleStartRef.current) / 1000;
          visibleStartRef.current = 0;
        }
      } else {
        visibleStartRef.current = Date.now();
      }
    };

    const flushOnLeave = () => {
      try {
        const path = lastEventPathRef.current || (location.pathname || "/");
        const dwellNow = visibleStartRef.current ? (Date.now() - visibleStartRef.current) / 1000 : 0;
        const total = Math.max(0, Math.round(accumulatedDwellRef.current + dwellNow));
        const subscriber_token = safeGet(SUBSCRIBER_KEY) || null;
        const session_id = getSessionId();
        const dossier_slug = dossierSlugFromPath(path);
        if (total >= 2) {
          sendOnUnload(`${API}/audience/event`, {
            session_id, route: path, dossier_slug,
            subscriber_token, kind: "read_time", value: total,
          });
        }
        if (maxScrollRef.current > 5) {
          sendOnUnload(`${API}/audience/event`, {
            session_id, route: path, dossier_slug,
            subscriber_token, kind: "scroll_depth",
            value: Math.round(maxScrollRef.current),
          });
        }
      } catch { /* silent */ }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flushOnLeave);
    window.addEventListener("beforeunload", flushOnLeave);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flushOnLeave);
      window.removeEventListener("beforeunload", flushOnLeave);
    };
  }, [shouldTrack]);

  return null;
};

/**
 * Lightweight helper that any public page can call to record an explicit
 * engagement event (node_click on the evidence board, source_click on an
 * outbound citation, etc.). Never wired into admin routes.
 */
export const trackAudienceEvent = (kind, opts = {}) => {
  try {
    if (typeof window === "undefined") return;
    const route = window.location?.pathname || "/";
    if (route.startsWith("/admin")) return;
    // Admin "Ignore my activity" — if the flag is on AND we have a logged-in
    // session token in localStorage, we assume the user is the admin and
    // bail. The backend also double-checks via Bearer token.
    try {
      const ignore = (localStorage.getItem("archive_admin_ignore_self") || "on") !== "off";
      const sessionToken = localStorage.getItem("archive_session_token");
      if (ignore && sessionToken) return;
    } catch (_) { /* noop */ }
    const payload = {
      session_id: getSessionId(),
      route,
      dossier_slug: dossierSlugFromPath(route),
      subscriber_token: safeGet(SUBSCRIBER_KEY) || null,
      kind,
      label: opts.label ? String(opts.label).slice(0, 240) : undefined,
      meta: opts.meta ? String(opts.meta).slice(0, 120) : undefined,
      value: typeof opts.value === "number" ? opts.value : undefined,
    };
    fireAndForget(`${API}/audience/event`, payload, null);
  } catch { /* silent */ }
};

/**
 * Read/write the admin "Ignore my activity" preference. Default ON.
 * Returns the current value (true = ignored).
 */
export const getAdminIgnoreSelf = () => {
  try {
    return (localStorage.getItem("archive_admin_ignore_self") || "on") !== "off";
  } catch { return true; }
};
export const setAdminIgnoreSelf = (on) => {
  try {
    localStorage.setItem("archive_admin_ignore_self", on ? "on" : "off");
  } catch { /* noop */ }
};

// Internal helpers exposed for tests/devtools only — not part of API.
export const __internal = { newSessionId, getSessionId, dossierSlugFromPath };
// Suppress unused-warning for legacy helper kept for parity.
void safeRemove;

export default LiveAudienceTracker;
